import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { EMPTY_WIZARD, type WizardState } from './validation';

const STORAGE_KEY = 'ssi.inspectionWizard';

function load(key: string, initial: WizardState): WizardState {
  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return initial;
    return { ...EMPTY_WIZARD, ...(JSON.parse(raw) as Partial<WizardState>) };
  } catch {
    return initial;
  }
}

export interface WizardEditing {
  id: string;
  version: number;
  number: string;
}

interface WizardApi {
  state: WizardState;
  update: (changes: Partial<WizardState>) => void;
  reset: () => void;
  /** Base route of the steps: /inspekcie/nova or /inspekcie/{id}/upravit. */
  basePath: string;
  /** Set when the wizard edits the basic data of an existing inspection. */
  editing: WizardEditing | null;
}

const WizardContext = createContext<WizardApi | null>(null);

/** Wizard state in React context, mirrored to sessionStorage so a refresh does not lose it. */
export function WizardProvider({
  children,
  storageKey = STORAGE_KEY,
  initial = EMPTY_WIZARD,
  basePath = '/inspekcie/nova',
  editing = null,
}: {
  children: ReactNode;
  storageKey?: string;
  initial?: WizardState;
  basePath?: string;
  editing?: WizardEditing | null;
}) {
  const [state, setState] = useState<WizardState>(() => load(storageKey, initial));
  useEffect(() => {
    try {
      if (state === EMPTY_WIZARD) window.sessionStorage.removeItem(storageKey);
      else window.sessionStorage.setItem(storageKey, JSON.stringify(state));
    } catch {
      // storage unavailable: the wizard still works within this page load
    }
  }, [state, storageKey]);
  const update = useCallback((changes: Partial<WizardState>) => setState((s) => ({ ...s, ...changes })), []);
  const reset = useCallback(() => {
    try {
      window.sessionStorage.removeItem(storageKey);
    } catch {
      // ignore
    }
    setState(EMPTY_WIZARD);
  }, [storageKey]);
  const api = useMemo(() => ({ state, update, reset, basePath, editing }), [state, update, reset, basePath, editing]);
  return <WizardContext.Provider value={api}>{children}</WizardContext.Provider>;
}

export function useWizard(): WizardApi {
  const ctx = useContext(WizardContext);
  if (!ctx) throw new Error('useWizard outside WizardProvider');
  return ctx;
}

export { STORAGE_KEY as WIZARD_STORAGE_KEY };
